import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [isMock, setIsMock] = useState(true);
  const [keyId, setKeyId] = useState("mock_key");
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [salespersons, setSalespersons] = useState([]);

  const visiblePackages = packages.filter((p) => (p.billing ?? "monthly") === billing);

  useEffect(() => {
    api.get("/payments/packages").then((r) => {
      setPackages(r.data.packages);
      setKeyId(r.data.key_id);
      setIsMock(!r.data.live);
    }).catch((err) => {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.msg || "Failed to load packages";
      toast.error(msg);
    });
    api.get("/salespersons").then((r) => setSalespersons(r.data)).catch(() => {});
    api.get("/payments/history").then((r) => setHistory(r.data)).catch((err) => {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.msg || "Failed to load history";
      toast.error(msg);
    });
  }, []);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const buy = async (pkg) => {
    setBusy(pkg.id);
    track("credit_purchase_started", { package_id: pkg.id, credits: pkg.credits, amount_inr: pkg.amount_inr });
    try {
      const { data: order } = await api.post("/payments/orders", { package_id: pkg.id });
      if (order.mock) {
        try {
          await api.post("/payments/verify", {
            razorpay_order_id: order.razorpay_order_id,
            razorpay_payment_id: `pay_mock_${Date.now()}`,
            razorpay_signature: "mock",
          });
          track("credit_purchase_completed", { package_id: pkg.id, credits: pkg.credits, amount_inr: pkg.amount_inr, mock: true });
          toast.success(`Added ${order.credits} credits (mock mode)`);
          await refresh();
          const { data } = await api.get("/payments/history");
          setHistory(data);
        } catch (verifyErr) {
          toast.error(verifyErr?.response?.data?.detail || "Payment verification failed");
        }
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Razorpay checkout failed to load");
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "NEIV",
        description: `${pkg.label} credit pack`,
        order_id: order.razorpay_order_id,
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", resp);
            track("credit_purchase_completed", { package_id: pkg.id, credits: pkg.credits, amount_inr: pkg.amount_inr, mock: false });
            toast.success(`Payment successful — ${pkg.credits} credits added`);
            await refresh();
            const { data } = await api.get("/payments/history");
            setHistory(data);
          } catch (verifyErr) {
            toast.error(verifyErr?.response?.data?.detail || "Payment verification failed");
          }
        },
        theme: { color: "#C86B53" },
      });
      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.msg) {
        toast.error(detail.msg);
      } else {
        toast.error("Purchase failed");
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-10" data-testid="billing-page">
      <div>
        <span className="overline">Billing</span>
        <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">Credits &amp; Billing</h1>
        <p className="mt-2 text-[#5C5F62]">Top up render credits. Each customer render costs 1 credit.</p>
      </div>

      <div className="neivai-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="flex flex-col sm:flex-row gap-8">
          <div>
            <p className="text-sm text-[#8A8D91]">Your balance</p>
            <p className="mt-1 text-3xl font-display font-semibold sm:text-4xl">{user?.credits ?? 0} credits</p>
          </div>
          <div className="w-px bg-[#E5E5E5] hidden sm:block" />
          <div>
            <p className="text-sm text-[#8A8D91]">Distributed to sales team</p>
            <p className="mt-1 text-3xl font-display font-semibold sm:text-4xl">
              {salespersons.reduce((sum, s) => sum + (s.credits ?? 0), 0)} credits
            </p>
          </div>
          <div className="w-px bg-[#E5E5E5] hidden sm:block" />
          <div>
            <p className="text-sm text-[#8A8D91]">Total credits</p>
            <p className="mt-1 text-3xl font-display font-semibold sm:text-4xl">
              {(user?.credits ?? 0) + salespersons.reduce((sum, s) => sum + (s.credits ?? 0), 0)} credits
            </p>
          </div>
        </div>
        {isMock && (
          <span className="px-3 py-1.5 rounded-full text-xs bg-[#F3F1EC] text-[#5C5F62] self-start sm:self-auto">Razorpay mock mode</span>
        )}
      </div>

      <div className="flex items-center justify-center gap-1 p-1 bg-[#F3F1EC] rounded-full w-fit">
        {["monthly", "yearly"].map((b) => (
          <button
            key={b}
            onClick={() => setBilling(b)}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              billing === b ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#5C5F62]"
            }`}
          >
            {b === "monthly" ? "Monthly" : "Yearly"}
            {b === "yearly" && <span className="ml-1.5 text-xs text-[#47795E] font-semibold">Save 20%</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {visiblePackages.map((p) => (
          <div key={p.id} className={`neivai-card p-6 sm:p-8 ${p.popular ? "ring-1 ring-[#C86B53]" : ""}`} data-testid={`package-${p.id}`}>
            {p.popular && <span className="overline">Most popular</span>}
            <h3 className="mt-2 text-3xl font-display font-medium">{p.label}</h3>
            <p className="mt-4 text-4xl font-display font-semibold">
              {p.billing === "yearly" ? <>₹{p.monthly_equiv}<span className="text-lg font-normal text-[#8A8D91]">/mo</span></> : <>₹{p.amount_inr}</>}
            </p>
            <p className="text-sm text-[#8A8D91]">
              {p.billing === "yearly" ? `₹${p.amount_inr} billed yearly · ${p.credits} credits` : `${p.credits} render credits`}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#5C5F62]">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#47795E]" /> Multi-product placement</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#47795E]" /> Region inpainting</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#47795E]" /> Download &amp; share</li>
            </ul>
            <button
              data-testid={`buy-${p.id}`}
              disabled={busy === p.id}
              onClick={() => buy(p)}
              className={`mt-8 w-full inline-flex items-center justify-center gap-2 ${p.popular ? "neivai-btn-primary" : "neivai-btn-secondary"} disabled:opacity-60`}
            >
              {busy === p.id ? "Processing…" : <><Sparkles className="h-4 w-4" /> Buy pack</>}
            </button>
          </div>
        ))}
      </div>

      <div className="neivai-card p-5 sm:p-8">
        <h3 className="text-2xl font-display font-medium">Payment history</h3>
        {history.length === 0 ? (
          <p className="mt-4 text-sm text-[#8A8D91]">No payments yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-left text-[#8A8D91] border-b border-[#E5E5E5]">
                  <th className="py-3">Date</th><th>Package</th><th>Credits</th><th>Amount</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-[#F0F0F0]">
                    <td className="py-3 whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td>
                    <td className="capitalize">{h.package_id}</td>
                    <td>{h.credits}</td>
                    <td>₹{h.amount_inr}</td>
                    <td><span className={`text-xs uppercase tracking-widest ${h.status === "paid" ? "text-[#47795E]" : "text-[#8A8D91]"}`}>{h.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-6 text-xs text-[#8A8D91]">Configured Razorpay key: <code className="font-mono">{keyId}</code></p>
      </div>
    </div>
  );
}
